import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '../components/ui/button';
import {
  Sparkles, Send, MessageSquarePlus, Trash2, ChevronLeft, GraduationCap,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric',
    });
  } catch (_) { return ''; }
};

const SAMPLE_PROMPTS = [
  { en: 'My F barre chord buzzes — how do I fix it?', it: 'Il mio barré di Fa ronza, come lo risolvo?' },
  { en: 'Suggest a 7-day plan to learn the pentatonic scale.', it: 'Suggerisci un piano di 7 giorni per imparare la scala pentatonica.' },
  { en: 'What are good warm-up exercises before a session?', it: 'Quali sono buoni esercizi di riscaldamento prima di una sessione?' },
  { en: "I can't switch between C and G fast enough.", it: 'Non riesco a cambiare velocemente tra Do e Sol.' },
];

const TutorPage = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [activeMessages, setActiveMessages] = useState([]);
  const [activeTitle, setActiveTitle] = useState(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingConv, setLoadingConv] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const scrollRef = useRef(null);

  const lang = i18n.language === 'it' ? 'it' : 'en';

  const fetchConversations = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tutor/conversations`);
      setConversations(data || []);
    } catch (err) {
      console.error('Error loading conversations:', err);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) fetchConversations();
  }, [isAuthenticated, fetchConversations]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [activeMessages, sending]);

  const openConversation = async (id) => {
    setLoadingConv(true);
    try {
      const { data } = await axios.get(`${API}/tutor/conversations/${id}`);
      setActiveConvId(data.id);
      setActiveMessages(data.messages || []);
      setActiveTitle(data.title);
    } catch (err) {
      toast.error(t('tutor.loadError'));
    } finally {
      setLoadingConv(false);
    }
  };

  const startNew = () => {
    setActiveConvId(null);
    setActiveMessages([]);
    setActiveTitle(null);
    setInput('');
  };

  const sendMessage = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || sending) return;

    setSending(true);
    const userMsg = { role: 'user', content: trimmed, ts: new Date().toISOString() };
    setActiveMessages((prev) => [...prev, userMsg]);
    setInput('');

    try {
      const { data } = await axios.post(`${API}/tutor/chat`, {
        message: trimmed,
        conversation_id: activeConvId,
      });
      const assistantMsg = {
        role: 'assistant',
        content: data.reply,
        ts: new Date().toISOString(),
      };
      setActiveMessages((prev) => [...prev, assistantMsg]);
      const wasNew = !activeConvId;
      setActiveConvId(data.conversation_id);
      if (wasNew) {
        setActiveTitle(data.title);
        fetchConversations();
      }
    } catch (err) {
      if (err.response?.status === 503) {
        toast.error(t('tutor.notConfigured'));
      } else if (err.response?.status === 401) {
        toast.error(t('premium.checkout.errorAuth'));
      } else {
        toast.error(t('tutor.sendError'));
      }
      // Rollback the user message
      setActiveMessages((prev) => prev.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  const deleteConversation = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm(t('tutor.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/tutor/conversations/${id}`);
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (activeConvId === id) startNew();
      toast.success(t('tutor.deleted'));
    } catch (err) {
      toast.error(t('tutor.deleteError'));
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-16" data-testid="tutor-page">
      <div className="flex h-[calc(100vh-64px)]">
        {/* Sidebar */}
        <aside
          className={`${sidebarOpen ? 'w-72' : 'w-0'} transition-[width] duration-200 overflow-hidden border-r border-white/5 bg-[#0E0E10] flex flex-col`}
        >
          <div className="p-4 border-b border-white/5">
            <Button
              onClick={startNew}
              className="w-full bg-gradient-to-r from-amber to-orange-500 text-black font-semibold"
              data-testid="tutor-new-chat"
            >
              <MessageSquarePlus className="w-4 h-4 mr-2" />
              {t('tutor.newChat')}
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 && (
              <p className="text-zinc-600 text-xs text-center px-4 py-6">
                {t('tutor.noHistory')}
              </p>
            )}
            {conversations.map((c) => (
              <button
                key={c.id}
                onClick={() => openConversation(c.id)}
                className={`w-full text-left px-4 py-3 border-b border-white/5 group flex items-start gap-2 ${
                  activeConvId === c.id ? 'bg-amber/10' : 'hover:bg-white/[0.02]'
                }`}
                data-testid={`tutor-conv-${c.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm truncate">{c.title || t('tutor.untitled')}</p>
                  <p className="text-zinc-600 text-xs">{formatTime(c.updated_at)}</p>
                </div>
                <button
                  onClick={(e) => deleteConversation(c.id, e)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-500 hover:text-red-400"
                  aria-label={t('common.delete')}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </button>
            ))}
          </div>
        </aside>

        {/* Main column */}
        <main className="flex-1 flex flex-col min-w-0">
          <header className="px-6 py-4 border-b border-white/5 flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen((v) => !v)}
              className="lg:hidden text-zinc-400 hover:text-white"
              aria-label="Toggle sidebar"
            >
              <ChevronLeft className={`w-5 h-5 transition-transform ${sidebarOpen ? '' : 'rotate-180'}`} />
            </button>
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber to-orange-500 flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-black" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-heading font-bold text-white text-lg">
                {activeTitle || t('tutor.title')}
              </h1>
              <p className="text-zinc-500 text-xs">{t('tutor.subtitle')}</p>
            </div>
          </header>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 sm:px-8 py-6">
            {!isAuthenticated && (
              <div className="max-w-md mx-auto text-center py-16">
                <Sparkles className="w-10 h-10 text-amber mx-auto mb-4" />
                <p className="text-zinc-300 mb-4">{t('tutor.loginRequired')}</p>
              </div>
            )}

            {isAuthenticated && activeMessages.length === 0 && !loadingConv && (
              <div className="max-w-2xl mx-auto text-center py-12">
                <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-amber/30 to-orange-500/20 border border-amber/30 flex items-center justify-center mb-6">
                  <GraduationCap className="w-10 h-10 text-amber" />
                </div>
                <h2 className="font-heading text-2xl font-bold text-white mb-2">
                  {t('tutor.welcome')}
                </h2>
                <p className="text-zinc-400 mb-8">{t('tutor.welcomeSubtitle')}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {SAMPLE_PROMPTS.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(p[lang])}
                      className="text-left p-4 rounded-xl bg-white/[0.03] border border-white/5 hover:border-amber/30 hover:bg-amber/5 transition-colors text-zinc-300 text-sm"
                    >
                      {p[lang]}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="max-w-3xl mx-auto space-y-6">
              {activeMessages.map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex gap-3 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {m.role === 'assistant' && (
                    <div className="shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br from-amber to-orange-500 flex items-center justify-center">
                      <GraduationCap className="w-5 h-5 text-black" />
                    </div>
                  )}
                  <div
                    className={`max-w-[85%] rounded-2xl px-5 py-3 ${
                      m.role === 'user'
                        ? 'bg-gradient-to-br from-blue/30 to-cyan-500/20 border border-blue/30 text-white'
                        : 'bg-white/[0.04] border border-white/10 text-zinc-200'
                    }`}
                  >
                    {m.role === 'assistant' ? (
                      <div className="text-[15px] leading-relaxed">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            h1: (props) => <h2 className="font-heading text-xl font-bold text-white mt-2 mb-3" {...props} />,
                            h2: (props) => <h3 className="font-heading text-lg font-bold text-amber mt-4 mb-2" {...props} />,
                            h3: (props) => <h4 className="font-heading font-semibold text-amber/90 mt-3 mb-1" {...props} />,
                            p: (props) => <p className="my-2" {...props} />,
                            ul: (props) => <ul className="list-disc list-inside my-2 space-y-1 marker:text-amber" {...props} />,
                            ol: (props) => <ol className="list-decimal list-inside my-2 space-y-1 marker:text-amber font-semibold" {...props} />,
                            strong: (props) => <strong className="text-white font-semibold" {...props} />,
                            code: ({ inline, ...p }) =>
                              inline
                                ? <code className="bg-white/10 text-amber rounded px-1 py-0.5 font-mono text-sm" {...p} />
                                : <code className="block bg-[#0A0A0A] text-amber rounded-lg p-3 font-mono text-sm overflow-x-auto my-2" {...p} />,
                          }}
                        >
                          {m.content}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <p className="whitespace-pre-wrap text-[15px]">{m.content}</p>
                    )}
                  </div>
                </motion.div>
              ))}

              {sending && (
                <div className="flex gap-3">
                  <div className="shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br from-amber to-orange-500 flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-black" />
                  </div>
                  <div className="bg-white/[0.04] border border-white/10 rounded-2xl px-5 py-3">
                    <div className="flex gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-amber animate-bounce" />
                      <div className="w-2 h-2 rounded-full bg-amber animate-bounce" style={{ animationDelay: '0.15s' }} />
                      <div className="w-2 h-2 rounded-full bg-amber animate-bounce" style={{ animationDelay: '0.3s' }} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Input bar */}
          {isAuthenticated && (
            <div className="border-t border-white/5 px-4 sm:px-8 py-4">
              <div className="max-w-3xl mx-auto">
                <div className="flex gap-2 items-end bg-[#121214] border border-white/10 rounded-2xl p-3 focus-within:border-amber/40 transition-colors">
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    rows={1}
                    placeholder={t('tutor.placeholder')}
                    disabled={sending}
                    className="flex-1 bg-transparent text-white placeholder:text-zinc-600 resize-none outline-none text-[15px] leading-relaxed max-h-32"
                    data-testid="tutor-input"
                  />
                  <Button
                    onClick={() => sendMessage()}
                    disabled={!input.trim() || sending}
                    className="bg-gradient-to-r from-amber to-orange-500 text-black font-semibold shrink-0"
                    data-testid="tutor-send"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-zinc-600 text-xs mt-2 text-center">
                  {t('tutor.disclaimer')}
                </p>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default TutorPage;
